import { Typography, FormInput, Button, Label } from '@ui/components';
import { CostCenterFormType } from '@pages/cost-centers/utils';
import { useHasPermission } from '@shared/hooks';
import { MaskitoOptions } from '@maskito/core';
import { Success } from '@ui/components/icons';
import { Masks } from '@helpers/constant';
import { FC } from 'react';

import { useCostCenterForm } from './use-cost-center-form';

export const CostCenterForm: FC<{ form: CostCenterFormType; editMode?: boolean }> = ({
  editMode = false,
  form,
}) => {
  const {
    getCompanyData,
    isCompanyDetailsLoading,
    copyCompanyDataHandler,
    isCompanyDataPending,
    isNipChecked,
    isNipValid,
    t,
  } = useCostCenterForm({ form, editMode });

  const hasEditPermission = useHasPermission('COST_CENTER_EDIT_COST_CENTER_ADDRESS', 'WRITE');

  return (
    <div className="grid grid-cols-6 gap-2 items-center">
      {!editMode && (
        <div className="col-span-6 flex justify-end">
          <Button onClick={copyCompanyDataHandler} variant="link" type="button">
            {t('COST_CENTERS.COPY_COMPANY_DATA')}
          </Button>
        </div>
      )}

      <Label className="col-span-2" htmlFor="name">
        <Typography variant="labelSmallBook" className="text-weak" element="label">
          {t('COST_CENTERS.NAME')}
        </Typography>
      </Label>
      <FormInput
        placeholder={t('COST_CENTERS.NAME')}
        disabled={!hasEditPermission}
        formControl={form.control}
        className="col-span-4"
        size="small"
        name="name"
      />

      {!!editMode && (
        <>
          <Label htmlFor="accountNumber" className="col-span-2">
            <Typography variant="labelSmallBook" className="text-weak" element="label">
              {t('COST_CENTERS.ACCOUNT_NUMBER')}
            </Typography>
          </Label>
          <FormInput
            placeholder={t('COST_CENTERS.ACCOUNT_NUMBER')}
            formControl={form.control}
            className="col-span-4"
            name="accountIban"
            disabled={true}
            size="small"
          />
        </>
      )}

      <Label className="col-span-2" htmlFor="nip">
        <Typography variant="labelSmallBook" className="text-weak" element="label">
          {t('COST_CENTERS.NIP')}
        </Typography>
      </Label>
      <FormInput
        componentProps={{
          id: `nip`,
          maskOptions: { mask: Masks.NIP } as MaskitoOptions,
        }}
        placeholder={t('COST_CENTERS.NIP')}
        formControl={form.control}
        className="col-span-4"
        disabled={!!editMode}
        size="small"
        name="nip"
      />

      {!editMode && (
        <Button
          disabled={!isNipValid || isCompanyDataPending || isCompanyDetailsLoading}
          isLoading={isCompanyDataPending || isCompanyDetailsLoading}
          className="col-span-4 col-start-3"
          onClick={getCompanyData}
          variant="tertiary"
          type="button"
        >
          {isNipChecked ? (
            <>
              {t('COST_CENTERS.COMPANY_DATA_FETCHED')}
              <Success className="size-4" />
            </>
          ) : (
            t('COST_CENTERS.DOWNLOAD_COMPANY_DATA')
          )}
        </Button>
      )}

      <Label className="col-span-2" htmlFor="companyName">
        <Typography variant="labelSmallBook" className="text-weak" element="label">
          {t('COST_CENTERS.COMPANY_NAME')}
        </Typography>
      </Label>
      <FormInput
        placeholder={t('COST_CENTERS.COMPANY_NAME')}
        formControl={form.control}
        className="col-span-4"
        disabled={!!editMode}
        name="companyName"
        size="small"
      />

      <Label className="col-span-2" htmlFor="street">
        <Typography variant="labelSmallBook" className="text-weak" element="label">
          {t('COST_CENTERS.STREET')}
        </Typography>
      </Label>
      <FormInput
        placeholder={t('COST_CENTERS.STREET')}
        disabled={!hasEditPermission}
        formControl={form.control}
        className="col-span-4"
        name="street"
        size="small"
      />

      <Label className="col-span-2" htmlFor="building">
        <Typography variant="labelSmallBook" className="text-weak" element="label">
          {t('COST_CENTERS.BUILDING')}
        </Typography>
      </Label>
      <FormInput
        placeholder={t('COST_CENTERS.BUILDING')}
        disabled={!hasEditPermission}
        formControl={form.control}
        className="col-span-4"
        name="buildingNumber"
        size="small"
      />

      <Label className="col-span-2" htmlFor="apartment">
        <Typography variant="labelSmallBook" className="text-weak" element="label">
          {t('COST_CENTERS.APARTMENT')}
        </Typography>
      </Label>
      <FormInput
        placeholder={t('COST_CENTERS.APARTMENT')}
        disabled={!hasEditPermission}
        formControl={form.control}
        className="col-span-4"
        name="apartmentNumber"
        size="small"
      />

      <Label className="col-span-2" htmlFor="zipCode">
        <Typography variant="labelSmallBook" className="text-weak" element="label">
          {t('COST_CENTERS.ZIP_CODE')}
        </Typography>
      </Label>
      <FormInput
        componentProps={{
          id: `zipCode`,
          maskOptions: { mask: Masks.ZIP_CODE } as MaskitoOptions,
        }}
        placeholder={t('COST_CENTERS.ZIP_CODE')}
        disabled={!hasEditPermission}
        formControl={form.control}
        className="col-span-4"
        name="zipCode"
        size="small"
      />

      <Label className="col-span-2" htmlFor="city">
        <Typography variant="labelSmallBook" className="text-weak" element="label">
          {t('COST_CENTERS.CITY')}
        </Typography>
      </Label>
      <FormInput
        placeholder={t('COST_CENTERS.CITY')}
        disabled={!hasEditPermission}
        formControl={form.control}
        className="col-span-4"
        size="small"
        name="city"
      />
    </div>
  );
};
